/**
 * The progress of a new design's first build (pages/Designer.tsx; pure, tested with node --test): the job
 * (`GET /v1/jobs/{id}`: status, current stage, overall progress) as a list of friendly steps, a percentage and a
 * plain-language error. User-facing words only: no model, method or threshold names. The words come from the
 * `designer` catalog through the caller's `t` (useT()).
 */
import type { MessageKey } from "../i18n/keys";

/** useT()'s translate function (or a test's): a catalog key and its ICU values. */
export type Tr = (key: MessageKey, values?: Record<string, string | number>) => string;

export type StageName = "imagery" | "roof_outline" | "roof_model" | "obstructions" | "setbacks" | "irradiance" | "layout" | "quote";
export type JobLike = {
  status: "queued" | "running" | "succeeded" | "failed" | "canceled";
  stages: string[];
  current_stage?: string | null;
  progress: number;
  queue_position?: number | null;
  eta_seconds?: number | null;
  error?: { detail?: string | null; title?: string | null } | null;
};
export type StepState = "done" | "active" | "pending" | "failed";

export const PIPELINE: StageName[] = ["imagery", "roof_outline", "roof_model", "obstructions", "setbacks", "irradiance", "layout", "quote"];

export const STAGE_LABELS: Record<StageName, MessageKey> = {
  imagery: "designer.progress.stage.imagery",
  roof_outline: "designer.progress.stage.roofOutline",
  roof_model: "designer.progress.stage.roofModel",
  obstructions: "designer.progress.stage.obstructions",
  setbacks: "designer.progress.stage.setbacks",
  irradiance: "designer.progress.stage.irradiance",
  layout: "designer.progress.stage.layout",
  quote: "designer.progress.stage.quote",
};

export function stageLabel(stage: string, t: Tr): string {
  const key = STAGE_LABELS[stage as StageName];
  return key ? t(key) : stage.replace(/_/g, " ");
}

/** Each step of the job (its stages, in pipeline order) with its state. */
export function steps(job: JobLike | null | undefined, t: Tr): { stage: string; label: string; state: StepState }[] {
  const names = job?.stages.length ? [...job.stages].sort((a, b) => order(a) - order(b)) : PIPELINE;
  const current = job?.current_stage ? names.indexOf(job.current_stage) : -1;
  return names.map((stage, i) => {
    let state: StepState = "pending";
    if (job?.status === "succeeded") state = "done";
    else if (job?.status === "failed" || job?.status === "canceled") state = i < current ? "done" : i === current ? "failed" : "pending";
    else if (job?.status === "running") state = current < 0 ? (i === 0 ? "active" : "pending") : i < current ? "done" : i === current ? "active" : "pending";
    return { stage, label: stageLabel(stage, t), state };
  });
}

function order(stage: string): number {
  const i = PIPELINE.indexOf(stage as StageName);
  return i < 0 ? PIPELINE.length : i;
}

/** 0–100, never 100 before the job succeeded. */
export function percent(job: JobLike | null | undefined): number {
  if (!job) return 0;
  if (job.status === "succeeded") return 100;
  return Math.max(0, Math.min(99, Math.round((job.progress || 0) * 100)));
}

/** One line under the bar: waiting, the current step, or what is left. */
export function headline(job: JobLike | null | undefined, t: Tr): string {
  if (!job || job.status === "queued") {
    const ahead = job?.queue_position ?? 0;
    return ahead > 0 ? t("designer.progress.waiting", { count: ahead }) : t("designer.progress.starting");
  }
  if (job.status === "succeeded") return t("designer.progress.ready");
  if (job.status === "failed" || job.status === "canceled")
    return job.current_stage ? t("designer.progress.stoppedAt", { step: stageLabel(job.current_stage, t) }) : t("designer.progress.stoppedBefore");
  return t("designer.progress.running", { step: stageLabel(job.current_stage || "imagery", t) });
}

/** When the job should finish (epoch ms), never later than shown before: the API's estimate grows while a step
 * runs longer than usual (a big roof), and a countdown that jumps up reads as going backwards. `previous` is the
 * last deadline shown for the same job (null at first). */
export function etaDeadline(previous: number | null, job: JobLike | null | undefined, now: number): number | null {
  const s = job?.eta_seconds;
  if (s == null || !(job?.status === "running" || job?.status === "queued") || s <= 0) return previous;
  const candidate = now + s * 1000;
  return previous == null ? candidate : Math.min(previous, candidate);
}

/** "about 40 s left" / "about 2 min left" until `deadline`, "almost done…" once it has passed, "" when unknown. */
export function remaining(job: JobLike | null | undefined, deadline: number | null, now: number, t: Tr): string {
  if (deadline == null || !(job?.status === "running" || job?.status === "queued")) return "";
  const s = (deadline - now) / 1000;
  if (s <= 5) return t("designer.progress.almostDone");
  return s < 90 ? t("designer.progress.secondsLeft", { seconds: Math.max(5, Math.round(s / 5) * 5) }) : t("designer.progress.minutesLeft", { minutes: Math.round(s / 60) });
}

/** m:ss of `ms` milliseconds. */
export function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** The failure in plain words, and whether trying again can help (or moving the pin). */
export function failure(job: JobLike, t: Tr): { message: string; movePin: boolean } {
  const detail = `${job.error?.detail ?? ""} ${job.error?.title ?? ""}`.toLowerCase();
  if (/no building|no data layers|not found|no imagery/.test(detail)) return { message: t("designer.progress.failure.noRoofData"), movePin: true };
  const step = job.current_stage ? stageLabel(job.current_stage, t) : null;
  if (/unavailable|timeout|timed out|upstream|503|502/.test(detail)) {
    return { message: step ? t("designer.progress.failure.serviceAt", { step }) : t("designer.progress.failure.service"), movePin: false };
  }
  return { message: step ? t("designer.progress.failure.otherAt", { step }) : t("designer.progress.failure.other"), movePin: false };
}
